// Scoring maths for inspection records, shared by the Inspection Summary report
// and the dashboard panels so the two can never disagree about what a "pass
// rate" is.
//
// A record's `data` object holds one entry per answered field, keyed by SCHEMA
// ITEM ID (not by label). Only some field types are scored — a header, a photo
// or a free-text note carries no pass/fail. Counting those would inflate the
// denominator and quietly lift every pass rate, so only explicit booleans
// qualify.

export function isScored(entry) {
  return entry != null && (entry.passed === true || entry.passed === false);
}

// Per-record item counts. Returns zeros for a record with no data object.
// ReadInspectionMetrics (server) applies the same rule in SQL.
export function countItems(record) {
  let answered = 0;
  let passed = 0;
  let failed = 0;
  if (record && record.data) {
    Object.keys(record.data).forEach(itemID => {
      let entry = record.data[itemID];
      if (!isScored(entry)) return;
      answered += 1;
      if (entry.passed) {
        passed += 1;
      } else {
        failed += 1;
      }
    });
  }
  return { answered: answered, passed: passed, failed: failed };
}

// Walks a set of records once and returns the totals a headline needs.
// `clean` counts submissions where every answered check passed.
export function tallyRecords(records) {
  let totals = {
    submissions: 0,
    answered: 0,
    passed: 0,
    failed: 0,
    clean: 0,
    failing: 0
  };

  (records || []).forEach(record => {
    let counts = countItems(record);
    totals.submissions += 1;
    totals.answered += counts.answered;
    totals.passed += counts.passed;
    totals.failed += counts.failed;
    if (counts.failed > 0) {
      totals.failing += 1;
    } else if (counts.answered > 0) {
      totals.clean += 1;
    }
  });

  return totals;
}

// Share of answered checks that failed, as a percentage. Null — not zero — when
// nothing was answered, so callers can render "no data" instead of a perfect
// score that was never earned.
export function failRate(totals) {
  if (!totals || !totals.answered) return null;
  return (totals.failed / totals.answered) * 100;
}

export function passRate(totals) {
  if (!totals || !totals.answered) return null;
  return (totals.passed / totals.answered) * 100;
}
